import { useState, useMemo } from "react";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Search, MapPin, Building2 } from "lucide-react";

export function DestinationPicker({
  clientId,
  clientAddress,
  selectedDest,
  onSelect,
  destinations,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search) return destinations;
    const q = search.toLowerCase();
    return destinations.filter(
      (d) =>
        (d.denominazione || "").toLowerCase().includes(q) ||
        (d.indirizzo || "").toLowerCase().includes(q) ||
        (d.citta || "").toLowerCase().includes(q) ||
        (d.cap || "").includes(q) ||
        (d.provincia || "").toLowerCase().includes(q)
    );
  }, [destinations, search]);

  const handleSelect = (dest) => {
    onSelect(dest);
    setOpen(false);
    setSearch("");
  };

  const handleSedeLegale = () => {
    onSelect(null);
    setOpen(false);
    setSearch("");
  };

  if (!clientId || destinations.length === 0) return null;

  return (
    <Card className="bg-[#18181b] border-white/5" data-testid="destination-picker">
      <CardContent className="p-4">
        <p className="text-xs text-zinc-500 mb-2">Destinazione merce</p>

        {/* Selected destination display */}
        {selectedDest ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
            <MapPin className="w-4 h-4 text-emerald-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-emerald-400 text-sm font-medium block truncate">
                {selectedDest.denominazione || selectedDest.indirizzo}
              </span>
              {selectedDest.denominazione && (
                <span className="text-zinc-500 text-xs block truncate">
                  {selectedDest.indirizzo}{selectedDest.citta ? ` - ${selectedDest.citta}` : ""}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-zinc-400 hover:text-white shrink-0"
              onClick={() => setOpen(!open)}
            >
              Cambia
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
            <Building2 className="w-4 h-4 text-amber-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-amber-400 text-sm font-medium">Sede legale</span>
              {clientAddress && <span className="text-zinc-500 text-xs block truncate">{clientAddress}</span>}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-zinc-400 hover:text-white shrink-0"
              onClick={() => setOpen(!open)}
            >
              Cambia
            </Button>
          </div>
        )}

        {/* Dropdown with search */}
        {open && (
          <div className="mt-2 bg-[#09090b] border border-white/10 rounded-lg overflow-hidden">
            {/* Search input */}
            <div className="p-2 border-b border-white/5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <Input
                  data-testid="dest-search"
                  type="text"
                  placeholder="Cerca indirizzo, comune, nome..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-9 bg-transparent border-white/10 text-white text-sm placeholder:text-zinc-600"
                  autoFocus
                />
              </div>
            </div>

            {/* Sede legale option */}
            <button
              className="w-full text-left px-4 py-2.5 hover:bg-white/5 text-sm border-b border-white/5"
              onClick={handleSedeLegale}
              data-testid="dest-sede-legale"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <span className="text-amber-400 font-medium">Sede legale</span>
              </div>
              {clientAddress && <span className="text-zinc-500 text-xs block ml-5.5">{clientAddress}</span>}
            </button>

            {/* Destination list */}
            <div className="max-h-48 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-4 py-3 text-center text-zinc-500 text-xs">Nessun risultato</div>
              ) : (
                filtered.map((d) => (
                  <button
                    key={d.id}
                    className="w-full text-left px-4 py-2.5 hover:bg-white/5 text-sm border-b border-white/5 last:border-0"
                    onClick={() => handleSelect(d)}
                    data-testid={`dest-option-${d.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                      <span className="text-white truncate">{d.denominazione || d.indirizzo}</span>
                    </div>
                    <span className="text-zinc-500 text-xs block ml-5.5 truncate">
                      {d.indirizzo}{d.citta ? ` - ${d.citta}` : ""} {d.cap || ""} {d.provincia || ""}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
